import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { AUTH_STORAGE_KEY } from '../services/config';
import { loginRequest, logoutRequest, meRequest, registerRequest } from '../services/auth';
import { readAuth, writeAuth, clearAuth } from '../services/apiClient';

const AuthContext = createContext(null);

export const HOME_FOR_ROLE = {
  admin: '/admin/users',
  premium: '/app/dashboard',
};

// Shown on the login page. These accounts are seeded in the backend
// (see backend/seed/reference_data.py).
export const DEMO_CREDENTIALS = [
  { email: 'admin@farmgate.ng', password: 'admin123', role: 'admin' },
  { email: 'premium@farmgate.ng', password: 'premium123', role: 'premium' },
];

export function AuthProvider({ children }) {
  // Hydrate immediately from the stored session so there's no logged-out flash.
  const [user, setUser] = useState(() => readAuth()?.user || null);

  // Keep tabs in sync (storage shape is { user, accessToken, refreshToken }).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== AUTH_STORAGE_KEY) return;
      try {
        setUser(e.newValue ? JSON.parse(e.newValue).user : null);
      } catch {
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // On mount, revalidate the session against the API (rehydrates name/plan,
  // and silently refreshes or logs out if the token is no longer valid).
  useEffect(() => {
    let active = true;
    if (!readAuth()?.accessToken) return undefined;
    meRequest()
      .then((fresh) => {
        if (!active || !fresh) return;
        const session = readAuth();
        if (session) writeAuth({ ...session, user: fresh });
        setUser(fresh);
      })
      .catch(() => {
        if (!active) return;
        clearAuth();
        setUser(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const session = await loginRequest(email, password);
      setUser(session.user);
      return { ok: true, user: session.user };
    } catch (e) {
      return { ok: false, error: e?.message || 'Invalid email or password.' };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const session = await registerRequest(name, email, password);
      setUser(session.user);
      return { ok: true, user: session.user };
    } catch (e) {
      return { ok: false, error: e?.message || 'Could not create your account.' };
    }
  }, []);

  // After confirming an address, refresh the cached user so the prompt goes
  // away without making the person sign in again.
  const markEmailVerified = useCallback(() => {
    setUser((u) => {
      if (!u || u.emailVerified) return u;
      const next = { ...u, emailVerified: true };
      const stored = readAuth();
      if (stored) writeAuth({ ...stored, user: next });
      return next;
    });
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
  }, []);

  // Keep the cached session in step after the account edits itself, so a new
  // name shows in the header immediately instead of after a sign-out.
  const applyUserPatch = useCallback((patch) => {
    setUser((u) => {
      if (!u) return u;
      const next = { ...u, ...patch };
      const stored = readAuth();
      if (stored) writeAuth({ ...stored, user: next });
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      register,
      markEmailVerified,
      applyUserPatch,
      role: user?.role || null,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'admin',
      isPremium: user?.role === 'premium',
      login,
      logout,
    }),
    [user, login, logout, register, markEmailVerified, applyUserPatch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Safe fallback - components that read auth before provider mounts get logged-out state.
    return {
      user: null,
      role: null,
      isAuthenticated: false,
      isAdmin: false,
      isPremium: false,
      login: async () => ({ ok: false }),
      logout: async () => {},
    };
  }
  return ctx;
}
